// ---- tool steps: a session's raw tool lines, read as steps ---------------------
//
// The runner writes one line per tool call: "Bash: <command>", "Read <path>",
// or a bare tool name, "mcp__alfieos__ask", optionally followed by what the call
// was about. A step is that line as a person reads it: a verb, the detail, and
// the kind of thing it did (which picks its icon).

export type StepKind =
  'run' | 'read' | 'edit' | 'search' | 'web' | 'talk' | 'ask' | 'do' | 'look' | 'plan' | 'other'

export interface Step {
  kind: StepKind
  label: string
  detail: string
}

/** Prime's own tools (the app's MCP server) by name: what each reads as. */
const MCP: Record<string, [StepKind, string]> = {
  ask: ['ask', 'Asked a question'],
  propose: ['ask', 'Made a card'],
  apply: ['do', 'Tidied up'],
  dismiss_card: ['do', 'Took down a card'],
  cards: ['look', 'Checked its cards'],
  search: ['search', 'Searched'],
  read: ['read', 'Read'],
  read_page: ['read', 'Read a page'],
  overview: ['look', 'Looked over the work'],
  workspaces: ['look', 'Looked at suites'],
  project_context: ['read', 'Read a project'],
  board: ['look', 'Checked the board'],
  schedule: ['look', 'Checked the schedule'],
  health: ['look', 'Checked health'],
  builds: ['look', 'Checked builds'],
  cluster: ['look', 'Checked the cluster'],
  activity: ['look', 'Read the activity log'],
  agents: ['look', 'Checked the agents'],
  read_agent: ['read', 'Read an agent'],
  read_pr: ['read', 'Read a pull request'],
  tell_agent: ['talk', 'Told an agent'],
  link_agent: ['do', 'Linked an agent'],
  tell_member: ['talk', 'Wrote to a member'],
  member_thread: ['read', "Read a member's thread"],
  ask_member_prime: ['talk', "Asked a member's Prime"],
  subscribe: ['do', 'Kept someone posted'],
  unsubscribe: ['do', 'Stopped a follow'],
  notify: ['talk', 'Sent a push'],
  watch: ['plan', 'Set a watch'],
  watches: ['look', 'Checked its watches'],
  unwatch: ['plan', 'Cancelled a watch'],
  remember: ['plan', 'Remembered'],
  memories: ['read', 'Read its memory'],
}

const CLAUDE: Record<string, [StepKind, string]> = {
  Read: ['read', 'Read'],
  Edit: ['edit', 'Edited'],
  MultiEdit: ['edit', 'Edited'],
  Write: ['edit', 'Wrote'],
  NotebookEdit: ['edit', 'Edited'],
  Grep: ['search', 'Searched for'],
  Glob: ['search', 'Found files'],
  WebFetch: ['web', 'Fetched'],
  WebSearch: ['web', 'Searched the web'],
  ToolSearch: ['other', 'Loaded tools'],
  Task: ['talk', 'Started a helper'],
  Agent: ['talk', 'Started a helper'],
}

const words = (name: string) => {
  const s = name.replace(/_/g, ' ')
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** A path reads as its last two parts: where it is, without /work/repo. */
const shortPath = (p: string) => {
  const parts = p.replace(/^\/work\/repo\//, '').split('/')
  return parts.length > 2 ? '…/' + parts.slice(-2).join('/') : parts.join('/')
}

export const stepOf = (line: string): Step => {
  const text = line.trim()
  if (text === 'Updated its plan') return { kind: 'plan', label: 'Updated its plan', detail: '' }
  const bash = /^Bash:\s*(.*)$/s.exec(text)
  if (bash) return { kind: 'run', label: 'Ran', detail: bash[1] ?? '' }
  const sp = text.indexOf(' ')
  const name = sp < 0 ? text : text.slice(0, sp)
  let detail = sp < 0 ? '' : text.slice(sp + 1).trim()
  const mcp = /^mcp__[^_]+(?:_[^_]+)*?__(.+)$/.exec(name)
  const tool = mcp?.[1]
  if (tool) {
    const [kind, label] = MCP[tool] ?? ['other', words(tool)]
    return { kind, label, detail }
  }
  const known = CLAUDE[name]
  if (known) {
    if (known[0] === 'read' || known[0] === 'edit') detail = detail && shortPath(detail)
    return { kind: known[0], label: known[1], detail }
  }
  // a bare tool name from somewhere else (the old thread's "ask")
  const [kind, label] = MCP[name] ?? ['other', words(name)]
  return { kind, label, detail }
}

/** The distinct things a run of steps did, in order, for its folded summary. */
export const stepSummary = (steps: Step[], max = 3): { labels: string[]; more: number } => {
  const seen: string[] = []
  for (const s of steps) if (!seen.includes(s.label)) seen.push(s.label)
  return { labels: seen.slice(0, max), more: Math.max(0, seen.length - max) }
}

const base = (p: string) => p.split('/').filter(Boolean).pop() ?? p

/** Commands that are only a launcher: the thing run is their next word ("pnpm build", "go test"). */
const LAUNCHERS = new Set([
  'pnpm',
  'npm',
  'npx',
  'yarn',
  'go',
  'git',
  'just',
  'cargo',
  'make',
  'docker',
  'kubectl',
])

/** The command a Bash line runs, as a person names it: "tsc", "pnpm build". */
const commandName = (cmd: string) => {
  const last =
    cmd
      .split(/&&|;|\|\|/)
      .map((s) => s.trim())
      .filter((s) => s && !/^cd\s/.test(s))[0] ?? cmd
  const words = last.split(/\s+/).filter((w) => !/^[A-Za-z_]+=/.test(w) && !w.startsWith('-'))
  const first = words[0] ?? ''
  const second = words[1]
  return LAUNCHERS.has(base(first)) && second ? `${base(first)} ${second}` : base(first)
}

const NOW: Record<StepKind, string> = {
  run: 'running',
  read: 'reading',
  edit: 'editing',
  search: 'searching',
  web: 'browsing',
  talk: 'talking',
  ask: 'asking',
  do: 'tidying up',
  look: 'looking',
  plan: 'planning',
  other: 'working',
}

/** A step as it reads while it is happening: "editing Thread.tsx", "running tsc". */
export const stepNow = (step: Step): string => {
  const verb = NOW[step.kind]
  if (step.kind === 'run') {
    const c = commandName(step.detail)
    return c ? `${verb} ${c}` : verb
  }
  if (step.kind === 'read' || step.kind === 'edit') return step.detail ? `${verb} ${base(step.detail)}` : verb
  if (step.kind === 'search' || step.kind === 'web') {
    const d = step.detail.replace(/\s+/g, ' ').trim()
    return d ? `${verb} ${d}` : verb
  }
  // Prime's own tools and the rest: their label, lower-cased ("Checked the board" reads as is)
  return step.kind === 'other' || step.kind === 'plan'
    ? `${verb}${step.label ? `: ${step.label.toLowerCase()}` : ''}`
    : step.label.toLowerCase()
}
